"use client";

import {
  BarChart3,
  Eye,
  LayoutDashboard,
  Mail,
  Plus,
  Settings,
  TrendingUp,
  Users,
} from "lucide-react";

import { cn } from "@/lib/utils";

import { BrowserFrame } from "./browser-frame";

const CHART = [
  32, 45, 28, 52, 68, 42, 58, 72, 48, 82, 65, 90, 55, 78, 95, 60, 85, 70,
  92, 88, 75, 98, 80, 64, 72, 86, 94, 78, 88, 100,
];

const FUNNELS = [
  {
    name: "SEO Checklist Funnel",
    template: "Lead Magnet",
    status: "Live",
    statusColor: "emerald",
    views: "1,247",
    leads: "89",
    rate: "7.1%",
  },
  {
    name: "Scale Your Agency Webinar",
    template: "Webinar",
    status: "Live",
    statusColor: "emerald",
    views: "834",
    leads: "62",
    rate: "7.4%",
  },
  {
    name: "Strategic Growth Session",
    template: "Consultation",
    status: "Live",
    statusColor: "emerald",
    views: "612",
    leads: "41",
    rate: "6.7%",
  },
  {
    name: "Copywriting Masterclass",
    template: "Course Waitlist",
    status: "Draft",
    statusColor: "amber",
    views: "—",
    leads: "—",
    rate: "—",
  },
  {
    name: "Fitness Transformation Guide",
    template: "Lead Magnet",
    status: "Live",
    statusColor: "emerald",
    views: "2,103",
    leads: "156",
    rate: "7.4%",
  },
];

const STATS = [
  { icon: Eye, label: "Total Views", value: "4,796", change: "+12.3%" },
  { icon: Users, label: "Total Leads", value: "348", change: "+8.1%" },
  { icon: TrendingUp, label: "Conv. Rate", value: "7.3%", change: "+0.4%" },
  { icon: BarChart3, label: "Active", value: "4", change: "" },
];

const NAV = [
  { icon: LayoutDashboard, label: "Dashboard", active: true },
  { icon: BarChart3, label: "Analytics", active: false },
  { icon: Users, label: "Leads", active: false },
  { icon: Mail, label: "Campaigns", active: false },
  { icon: Settings, label: "Settings", active: false },
];

interface FakeDashboardProps {
  /** When true, the "Publish" success toast appears in the corner. */
  showPublishedToast?: boolean;
  /** Highlight the New Funnel button (used by the V1 animation). */
  pulseCta?: boolean;
  className?: string;
}

export function FakeDashboard({
  showPublishedToast = false,
  pulseCta = false,
  className,
}: FakeDashboardProps) {
  return (
    <BrowserFrame
      url="app.funnlr.co/dashboard"
      className={cn("w-full", className)}
    >
      <div className="flex h-[460px] sm:h-[520px] lg:h-[560px]">
        {/* Sidebar */}
        <div className="hidden w-44 shrink-0 flex-col bg-[#0F172A] px-3 py-4 sm:flex">
          <div className="mb-5 flex items-center gap-2 px-2">
            <div className="h-6 w-6 rounded-md bg-gradient-to-br from-violet-400 to-violet-600" />
            <span className="text-[12px] font-semibold tracking-tight text-white">
              Funnlr
            </span>
          </div>

          <div className="flex flex-col gap-0.5">
            {NAV.map((item) => (
              <div
                key={item.label}
                className={cn(
                  "flex items-center gap-2 rounded-md px-2.5 py-1.5 text-[11px] font-medium",
                  item.active
                    ? "bg-white/10 text-white"
                    : "text-slate-400",
                )}
              >
                <item.icon className="h-3.5 w-3.5" />
                <span>{item.label}</span>
              </div>
            ))}
          </div>

          <div className="mt-auto flex items-center gap-2 rounded-md px-2.5 py-2">
            <div className="h-6 w-6 rounded-full bg-gradient-to-br from-violet-400 to-violet-600 ring-2 ring-violet-500/30" />
            <div>
              <p className="text-[10px] font-semibold text-white">Jane Smith</p>
              <p className="text-[9px] text-slate-500">Lifetime · Pro</p>
            </div>
          </div>
        </div>

        {/* Main */}
        <div className="relative flex-1 overflow-hidden bg-slate-50/60 p-4 sm:p-5">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h2 className="font-heading text-[15px] font-semibold tracking-tight text-slate-900">
                Your Funnels
              </h2>
              <p className="text-[10px] text-slate-400">
                3 of 10 live · updated just now
              </p>
            </div>
            <div className="flex items-center gap-2">
              <div className="hidden items-center gap-1.5 rounded-md bg-white px-2.5 py-1 ring-1 ring-slate-200 sm:flex">
                <Eye className="h-3 w-3 text-slate-400" />
                <span className="text-[10px] text-slate-500">Search…</span>
              </div>
              <div
                className={cn(
                  "flex items-center gap-1.5 rounded-md bg-violet-600 px-2.5 py-1 shadow-sm shadow-violet-500/20",
                  pulseCta && "animate-pulse",
                )}
              >
                <Plus className="h-3 w-3 text-white" />
                <span className="text-[10px] font-semibold text-white">
                  New Funnel
                </span>
              </div>
            </div>
          </div>

          <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-4 sm:gap-2.5">
            {STATS.map((s) => (
              <div
                key={s.label}
                className="rounded-lg border border-slate-200/80 bg-white p-2.5"
              >
                <div className="flex items-center gap-1.5">
                  <div className="flex h-5 w-5 items-center justify-center rounded-md bg-slate-100/80">
                    <s.icon className="h-3 w-3 text-slate-400" />
                  </div>
                  <span className="text-[9px] font-medium tracking-tight text-slate-400">
                    {s.label}
                  </span>
                </div>
                <div className="mt-1.5 flex items-baseline gap-1.5">
                  <span className="font-heading text-base font-bold text-slate-900">
                    {s.value}
                  </span>
                  {s.change && (
                    <span className="text-[9px] font-semibold text-emerald-500">
                      {s.change}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="mb-4 rounded-lg border border-slate-200/80 bg-white p-3.5">
            <div className="mb-3 flex items-center justify-between">
              <div>
                <p className="text-[11px] font-semibold text-slate-900">
                  Views · Leads
                </p>
                <p className="text-[9px] text-slate-400">Last 30 days</p>
              </div>
              <div className="flex gap-0.5 rounded-md bg-slate-100 p-0.5">
                {["7d", "30d", "90d"].map((p) => (
                  <span
                    key={p}
                    className={cn(
                      "rounded px-1.5 py-0.5 text-[9px] font-medium",
                      p === "30d"
                        ? "bg-white text-slate-900 shadow-sm"
                        : "text-slate-400",
                    )}
                  >
                    {p}
                  </span>
                ))}
              </div>
            </div>
            <div className="flex h-16 items-end gap-[2px]">
              {CHART.map((h, i) => (
                <div
                  key={i}
                  className="flex-1 rounded-[1px] bg-violet-500/85"
                  style={{ height: `${h}%` }}
                />
              ))}
            </div>
          </div>

          <div className="overflow-hidden rounded-lg border border-slate-200/80 bg-white">
            <div className="grid grid-cols-[1fr_70px_60px_56px] gap-2 border-b border-slate-100 px-3 py-2 sm:grid-cols-[1fr_80px_70px_56px_56px_56px]">
              {["Name", "Template", "Status", "Views", "Leads", "Conv."].map(
                (h, i) => (
                  <span
                    key={h}
                    className={cn(
                      "text-[8px] font-semibold uppercase tracking-wider text-slate-400",
                      i >= 3 && "hidden sm:block",
                    )}
                  >
                    {h}
                  </span>
                ),
              )}
            </div>
            {FUNNELS.map((f) => (
              <div
                key={f.name}
                className="grid grid-cols-[1fr_70px_60px_56px] items-center gap-2 border-b border-slate-50 px-3 py-2 last:border-0 sm:grid-cols-[1fr_80px_70px_56px_56px_56px]"
              >
                <span className="truncate text-[10px] font-medium text-slate-900">
                  {f.name}
                </span>
                <span className="text-[10px] text-slate-500">{f.template}</span>
                <span>
                  <span
                    className={cn(
                      "inline-block rounded-full px-1.5 py-0.5 text-[8px] font-semibold",
                      f.statusColor === "emerald"
                        ? "bg-emerald-50 text-emerald-600"
                        : "bg-amber-50 text-amber-600",
                    )}
                  >
                    {f.status}
                  </span>
                </span>
                <span className="text-[10px] text-slate-600">{f.views}</span>
                <span className="hidden text-[10px] text-slate-600 sm:block">
                  {f.leads}
                </span>
                <span className="hidden text-[10px] text-slate-600 sm:block">
                  {f.rate}
                </span>
              </div>
            ))}
          </div>

          {showPublishedToast && (
            <div className="absolute bottom-4 right-4 flex items-center gap-2 rounded-lg bg-slate-900 px-3 py-2 text-[11px] font-medium text-white shadow-lg ring-1 ring-slate-900/50">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-400" />
              Funnel published · live now
            </div>
          )}
        </div>
      </div>
    </BrowserFrame>
  );
}
